import { memo, useEffect, useState } from 'react'
import { GenericCandidateLite } from '../api/role/models/CandidateDto'
import { MapExtra } from '../utils/MapExtra'
import { CompanyDto } from '../api/company/models/CompanyDto'
import { ResolvedPersonLiteDto } from '../api/network/models/getNetworkScopedDtos'
import { PersonDto, PersonLiteDto } from '../api/person/models/PersonDto'
import { transformNetworkScopedRes } from '../utils/networkTransformUtils'
import PaginationControls from './common/PaginationControls'

interface CandidateResultListProps<TCandidate extends GenericCandidateLite> {
    candidates: TCandidate[]
    personCache: MapExtra<string, PersonDto>
    personLiteCache: MapExtra<string, PersonLiteDto>
    resolvedPersonLites: ResolvedPersonLiteDto[]
    companyCache: MapExtra<string, CompanyDto>
    currentPageNumber: number
    perPage: number
    totalResultCount: number
    pageChanged: (pageNumber: number) => void
    perPageChanged: (perPage: number) => void
    onCandidateSelection?: (candidateId: string) => void
    roleId?: string
    getCandidateRank: (candidate: TCandidate) => number
    ResultItemComponent: React.ComponentType<{
        candidate: TCandidate
        person: PersonDto
        companyCache: MapExtra<string, CompanyDto>
        personLiteCache: MapExtra<string, PersonLiteDto>
        transformedResolvedPerson?: TransformedResolvedPerson
        onCandidateSelection?: (candidateId: string) => void
        roleId?: string
    }>
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200]
const DEFAULT_PAGE_SIZE = 25

function CandidateResultListComponent<TCandidate extends GenericCandidateLite>({
    candidates,
    personCache,
    personLiteCache,
    resolvedPersonLites,
    companyCache,
    currentPageNumber,
    perPage,
    totalResultCount,
    pageChanged,
    perPageChanged,
    onCandidateSelection,
    roleId,
    getCandidateRank,
    ResultItemComponent,
}: CandidateResultListProps<TCandidate>): React.JSX.Element {
    const [transformedResolvedPersons, setTransformedResolvedPersons] =
        useState<TransformedResolvedPerson[]>([])

    useEffect(() => {
        setTransformedResolvedPersons(
            transformNetworkScopedRes(resolvedPersonLites),
        )
    }, [resolvedPersonLites])

    return (
        <div>
            <div className="border-b border-gray-200 pb-4 mb-4">
                <PaginationControls
                    totalItems={totalResultCount}
                    currentPage={currentPageNumber}
                    onPageChange={pageChanged}
                    pageSize={perPage}
                    defaultPageSize={DEFAULT_PAGE_SIZE}
                    onPageSizeChange={perPageChanged}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    textSizeClassName="text-base"
                    className="mt-6"
                />
            </div>

            {[...candidates]
                .sort((a, b) => getCandidateRank(a) - getCandidateRank(b))
                .map((candidate) => {
                    const person = personCache.get(candidate.candidatePersonId)
                    if (!person) {
                        throw new Error(
                            `Unable to find person for candidate: ${candidate.candidatePersonId}`,
                        )
                    }

                    const transformed = transformedResolvedPersons.find(
                        (p) => p.personId === candidate.candidatePersonId,
                    )

                    return (
                        <ResultItemComponent
                            key={candidate.candidatePersonId}
                            candidate={candidate}
                            person={person}
                            companyCache={companyCache}
                            personLiteCache={personLiteCache}
                            transformedResolvedPerson={transformed}
                            onCandidateSelection={onCandidateSelection}
                            roleId={roleId}
                        />
                    )
                })}
            <PaginationControls
                totalItems={totalResultCount}
                currentPage={currentPageNumber}
                onPageChange={pageChanged}
                pageSize={perPage}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                onPageSizeChange={perPageChanged}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                textSizeClassName="text-base"
                className="mt-6"
            />
        </div>
    )
}

const CandidateResultList = memo(CandidateResultListComponent)
CandidateResultList.displayName = 'CandidateResultList'

export default CandidateResultList

export type TransformedResolvedPerson = {
    personId: string
    companies: TransformedCompany[]
}

export type TransformedCompany = {
    companyId: string
    connections: TransformedConnection[]
}

export type TransformedConnection = {
    seedPersonId: string
    resolvedPersonJobTitles: string[]
    seedPersonJobTitles: string[]
    cumulativeOverlapMs: number
    overallStart: Date
    overallEnd: Date
}
